import axios from "axios";
import env from "../env";

const getAssetsUrl = (assetId) => {
  const baseUrl = `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/appsupport/assets`;
  return assetId ? `${baseUrl}/${encodeURIComponent(assetId)}` : baseUrl;
};

const buildAssetQueryParams = (tag, resolution) => {
  const params = {};
  const trimmedTag = tag?.trim();

  if (trimmedTag) {
    params.tag = trimmedTag;
  }

  if (resolution) {
    params.resolution = resolution;
  }

  return params;
};

const createNotFoundError = () => {
  const error = new Error("Asset not found.");
  error.response = { status: 404, data: { message: "Asset not found." } };
  return error;
};

/** DELETE .../assets/{assetId}. Optional tag and resolution query params delete that file only. */
const performDeleteAsset = (assetId, tag, resolution) => {
  return axios.delete(getAssetsUrl(assetId?.trim()), {
    headers: {
      "Content-Type": "application/json",
    },
    params: buildAssetQueryParams(tag, resolution),
    paramsSerializer: {
      encode: (value) => encodeURIComponent(value),
    },
  });
};

export const uploadAsset = (formData) => {
  return axios.post(getAssetsUrl(), formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/** PUT .../appsupport/assets. Metadata sends assetId, name, and description. A file update also sends file, tag, and resolution. */
export const updateAsset = (formData) => {
  return axios.put(getAssetsUrl(), formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const resolveAssetViewUrl = (data) => {
  if (!data) return "";

  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (typeof data === "object") {
    return (
      data.url ||
      data.downloadUrl ||
      data.signedUrl ||
      data.assetUrl ||
      ""
    );
  }

  return "";
};

export const resolveAssetViewContentType = (data) => {
  if (!data || typeof data !== "object") return "";

  return (
    data.contentType ||
    data.content_type ||
    data.mimeType ||
    data.mime_type ||
    data.fileContentType ||
    ""
  );
};

export const resolveAssetViewMeta = (data) => ({
  url: resolveAssetViewUrl(data),
  contentType: resolveAssetViewContentType(data),
});

export const getAssetErrorMessage = (
  error,
  fallback = "Request failed",
  notFoundMessage = "Asset not found. Please verify the Asset ID, Tag, and Resolution.",
) => {
  const status = error?.response?.status;
  const errMsg =
    error?.userMessage ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  if (
    status === 404 ||
    (typeof errMsg === "string" &&
      (errMsg.toLowerCase().includes("not found") ||
        errMsg.toLowerCase().includes("does not exist")))
  ) {
    return notFoundMessage;
  }

  if (
    typeof errMsg === "string" &&
    errMsg.toLowerCase().includes("already exist")
  ) {
    return errMsg;
  }

  return errMsg;
};

const pickAssetRecord = (data, assetId) => {
  const list = unwrapAssetList(data);
  const trimmedAssetId = assetId?.trim();
  const match = trimmedAssetId
    ? list.find((item) => String(item?.assetId || item?.id || "").trim() === trimmedAssetId)
    : null;

  if (match) {
    return match;
  }

  if (list.length === 1) {
    return list[0];
  }

  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    (data.assetId || data.id || data.tags || data.resolutions)
  ) {
    return data;
  }

  return null;
};

/** GET .../assets?assetId= . One asset from the list API. */
export const getAssetMetadata = async (assetId) => {
  const trimmedAssetId = assetId?.trim();

  if (!trimmedAssetId) {
    throw createNotFoundError();
  }

  const response = await axios.get(getAssetsUrl(), {
    headers: {
      "Content-Type": "application/json",
    },
    params: {
      assetId: trimmedAssetId,
      page: 1,
      pageSize: 1,
    },
  });
  const record = pickAssetRecord(response?.data, trimmedAssetId);

  if (!record) {
    throw createNotFoundError();
  }

  return { data: record };
};

export const viewAsset = (assetId, tag, resolution) => {
  return axios.get(getAssetsUrl(assetId), {
    headers: {
      "Content-Type": "application/json",
    },
    params: buildAssetQueryParams(tag, resolution),
  });
};

export const deleteAsset = async (assetId, tag, resolution) => {
  const trimmedAssetId = assetId?.trim();

  if (!trimmedAssetId) {
    throw createNotFoundError();
  }

  return performDeleteAsset(trimmedAssetId, tag, resolution);
};

export const extractAssetId = (data, fallback = "") => {
  if (!data) {
    return fallback;
  }

  if (typeof data === "string") {
    return data;
  }

  return data.assetId || data.id || fallback;
};

const resolutionNamesFrom = (resolutions) => {
  if (Array.isArray(resolutions)) {
    return resolutions.map((value) => String(value).trim()).filter(Boolean);
  }

  if (resolutions && typeof resolutions === "object") {
    return Object.keys(resolutions).map((value) => value.trim()).filter(Boolean);
  }

  if (typeof resolutions === "string" && resolutions.trim()) {
    return [resolutions.trim()];
  }

  return [];
};

const tagNamesFrom = (tags, fallbackTag = "") => {
  if (Array.isArray(tags)) {
    return tags
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }

        if (item && typeof item === "object") {
          return String(item.tag || "").trim();
        }

        return "";
      })
      .filter(Boolean);
  }

  if (typeof fallbackTag === "string" && fallbackTag.trim()) {
    return [fallbackTag.trim()];
  }

  return [];
};

const metadataRecordsFrom = (data) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    if (data.some((item) => typeof item === "string" || item?.tag || item?.resolutions)) {
      return [{ tags: data }];
    }

    return data.filter((item) => item && typeof item === "object");
  }

  if (typeof data !== "object") {
    return [];
  }

  if (data.tags || data.resolutions || data.tag) {
    return [data];
  }

  for (const key of ["data", "result", "asset", "metadata"]) {
    const nested = metadataRecordsFrom(data[key]);
    if (nested.length) {
      return nested;
    }
  }

  return [];
};

const pathSegments = (value) => {
  const text = String(value || "").split("?")[0].split("#")[0].trim();

  if (!text) {
    return [];
  }

  try {
    const path = /^https?:\/\//i.test(text) ? new URL(text).pathname : text;
    return decodeURIComponent(path).split("/").filter(Boolean);
  } catch {
    return text.split("/").filter(Boolean);
  }
};

const segmentAfterAssetId = (value, assetId) => {
  const trimmedAssetId = String(assetId || "").trim();
  const parts = pathSegments(value);

  if (!trimmedAssetId) {
    return "";
  }

  const assetIndex = parts.indexOf(trimmedAssetId);
  return assetIndex >= 0 ? parts[assetIndex + 1] || "" : "";
};

const resolutionPathsFrom = (resolutions) => {
  if (Array.isArray(resolutions)) {
    return resolutions
      .map((value) => ({ resolution: String(value).trim(), path: "" }))
      .filter((entry) => entry.resolution);
  }

  if (resolutions && typeof resolutions === "object") {
    return Object.entries(resolutions)
      .map(([resolution, path]) => ({
        resolution: String(resolution).trim(),
        path: typeof path === "string" ? path.trim() : "",
      }))
      .filter((entry) => entry.resolution);
  }

  if (typeof resolutions === "string" && resolutions.trim()) {
    return [{ resolution: resolutions.trim(), path: "" }];
  }

  return [];
};

const addTagResolution = (grouped, tag, resolutions) => {
  const trimmedTag = typeof tag === "string" ? tag.trim() : "";

  if (!trimmedTag && !resolutions.length) {
    return;
  }

  const current = grouped.get(trimmedTag) || [];
  resolutions.forEach((resolution) => {
    if (resolution && !current.includes(resolution)) {
      current.push(resolution);
    }
  });
  grouped.set(trimmedTag, current);
};

/** Tag/resolution entries from GET .../assets/metadata/{assetId} or a list item. */
export const parseAssetTagMetadata = (data) => {
  const grouped = new Map();

  metadataRecordsFrom(data).forEach((record) => {
    const sharedResolutions = resolutionNamesFrom(record.resolutions);
    const objectTags = Array.isArray(record.tags)
      ? record.tags.filter((item) => item && typeof item === "object" && item.tag)
      : [];

    if (objectTags.length) {
      objectTags.forEach((item) => {
        const ownResolutions = resolutionNamesFrom(item.resolutions);
        addTagResolution(
          grouped,
          item.tag,
          ownResolutions.length ? ownResolutions : sharedResolutions
        );
      });
      return;
    }

    const names = tagNamesFrom(record.tags, record.tag);
    if (!names.length) {
      addTagResolution(grouped, "", sharedResolutions);
      return;
    }

    const assetId = String(record.assetId || record.id || data?.assetId || data?.id || "").trim();
    const owned = resolutionPathsFrom(record.resolutions)
      .map((entry) => ({
        ...entry,
        owner: segmentAfterAssetId(entry.path, assetId),
      }))
      .filter((entry) => names.includes(entry.owner));

    if (owned.length) {
      names.forEach((tag) => {
        addTagResolution(
          grouped,
          tag,
          owned.filter((entry) => entry.owner === tag).map((entry) => entry.resolution)
        );
      });
      return;
    }

    names.forEach((tag) => addTagResolution(grouped, tag, sharedResolutions));
  });

  return Array.from(grouped.entries())
    .map(([tag, resolutions]) => ({ tag, resolutions }))
    .filter((item) => item.tag || item.resolutions.length);
};

const sharedFileKey = (path, resolution) => {
  const parts = pathSegments(path);

  if (!parts.length) {
    return "";
  }

  const resolutionName = String(resolution || "").trim().toLowerCase();
  const resolutionIndex = parts.findIndex((part) => part.toLowerCase() === resolutionName);
  const comparable = resolutionIndex >= 0
    ? [...parts.slice(0, resolutionIndex), ...parts.slice(resolutionIndex + 1)]
    : parts;

  return comparable.join("/").toLowerCase();
};

/** One image per resolution, placed under the tag stored in its path. */
export const groupResolutionsByTag = (data) => {
  const groups = new Map();
  const fileGroups = new Map();
  const pathByTag = new Map();
  const tagOrder = [];

  const rememberTag = (tag) => {
    const trimmedTag = typeof tag === "string" ? tag.trim() : "";

    if (!groups.has(trimmedTag)) {
      groups.set(trimmedTag, []);
      fileGroups.set(trimmedTag, new Map());
      tagOrder.push(trimmedTag);
    }
  };

  const rememberFile = (tag, resolution, path) => {
    const files = fileGroups.get(tag) || new Map();
    const key = sharedFileKey(path, resolution) || `resolution:${resolution}`;
    const fallbackKey = `resolution:${resolution}`;

    if (path && key !== fallbackKey && files.has(fallbackKey)) {
      const remaining = (files.get(fallbackKey) || []).filter((item) => item !== resolution);
      if (remaining.length) {
        files.set(fallbackKey, remaining);
      } else {
        files.delete(fallbackKey);
      }
    }

    const current = files.get(key) || [];

    if (!current.includes(resolution)) {
      current.push(resolution);
    }

    files.set(key, current);
    fileGroups.set(tag, files);

    if (path) {
      const paths = pathByTag.get(tag) || new Map();
      if (!paths.get(resolution)) {
        paths.set(resolution, path);
      }
      pathByTag.set(tag, paths);
    }
  };

  metadataRecordsFrom(data).forEach((record) => {
    const assetId = String(record.assetId || record.id || data?.assetId || data?.id || "").trim();
    tagNamesFrom(record.tags, record.tag).forEach(rememberTag);

    const objectTags = Array.isArray(record.tags)
      ? record.tags.filter((item) => item && typeof item === "object" && item.resolutions)
      : [];

    if (objectTags.length) {
      objectTags.forEach((item) => {
        const tag = String(item.tag || "").trim();
        rememberTag(tag);
        const taggedResolutions = resolutionPathsFrom(item.resolutions);
        const resolutionEntries = taggedResolutions.length
          ? taggedResolutions
          : resolutionNamesFrom(item.resolutions).map((resolution) => ({ resolution, path: "" }));
        resolutionEntries.forEach(({ resolution, path }) => {
          const current = groups.get(tag);
          if (!current.includes(resolution)) {
            current.push(resolution);
          }
          rememberFile(tag, resolution, path);
        });
      });
      resolutionPathsFrom(record.resolutions).forEach(({ resolution, path }) => {
        if (!path) {
          return;
        }

        tagOrder.forEach((tagName) => {
          if ((groups.get(tagName) || []).includes(resolution)) {
            rememberFile(tagName, resolution, path);
          }
        });
      });
      return;
    }

    resolutionPathsFrom(record.resolutions).forEach(({ resolution, path }) => {
      const owner = segmentAfterAssetId(path, assetId);
      const tag = owner && owner.toLowerCase() !== resolution.toLowerCase() ? owner : "";
      rememberTag(tag);
      const current = groups.get(tag);
      if (!current.includes(resolution)) {
        current.push(resolution);
      }
      rememberFile(tag, resolution, path);
    });
  });

  return tagOrder
    .map((tag) => ({
      tag,
      resolutions: groups.get(tag) || [],
      resolutionPaths: (groups.get(tag) || []).map((resolution) => ({
        resolution,
        path: pathByTag.get(tag)?.get(resolution) || "",
      })),
      files: Array.from(fileGroups.get(tag)?.values() || []).map((resolutions) => ({ resolutions })),
    }))
    .filter((item) => item.tag || item.resolutions.length);
};

const httpUrl = (value) => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : "";
};

export const resolveResolutionAssetUrl = (data, resolution) => {
  const direct = resolveAssetViewUrl(data);
  if (direct && httpUrl(direct)) {
    return direct;
  }

  const map = data?.resolutions;
  if (map && typeof map === "object" && !Array.isArray(map)) {
    return httpUrl(map[resolution]);
  }

  return "";
};

const imageTypeFromBytes = (bytes) => {
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return "image/png";
  }

  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    return "image/jpeg";
  }

  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return "image/gif";
  }

  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46
  ) {
    return "image/webp";
  }

  return "";
};

const fileFromBlob = async (blob, filename) => {
  if (!blob?.size) {
    return null;
  }

  let type = String(blob.type || "")
    .split(";")[0]
    .trim()
    .toLowerCase();

  if (!type.startsWith("image/")) {
    const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
    type = imageTypeFromBytes(bytes);
  }

  if (!type.startsWith("image/")) {
    return null;
  }

  const extension = type.split("/")[1] || "img";
  const baseName = String(filename || "asset").trim() || "asset";
  const hasExtension = /\.[a-z0-9]+$/i.test(baseName);

  return new File([blob], hasExtension ? baseName : `${baseName}.${extension}`, { type });
};

const readAssetBlob = async (assetId, tag, resolution) => {
  const response = await axios.get(getAssetsUrl(assetId), {
    params: buildAssetQueryParams(tag, resolution),
    responseType: "blob",
    headers: {
      Accept: "image/png,image/jpeg,image/webp,image/gif,application/octet-stream,*/*;q=0.8",
    },
  });
  const blob = response?.data;
  const headerType = String(blob?.type || response?.headers?.["content-type"] || "")
    .split(";")[0]
    .trim()
    .toLowerCase();

  return { blob, headerType };
};

const displayFileName = (value) => {
  const text = String(value || "").split("?")[0].split("#")[0].trim();

  if (!text) {
    return "";
  }

  try {
    const path = /^https?:\/\//i.test(text) ? new URL(text).pathname : text;
    return decodeURIComponent(path.split("/").filter(Boolean).pop() || "");
  } catch {
    return "";
  }
};

const isResolutionLabel = (name, resolution) =>
  String(name || "").trim().toLowerCase() === String(resolution || "").trim().toLowerCase();

const storedImageName = (json, resolution, location, url) => {
  const namedFields = [
    json?.fileName,
    json?.filename,
    json?.originalFileName,
    json?.originalFilename,
    json?.imageName,
    json?.assetFileName,
  ];
  const candidates = [
    ...namedFields.map((value) => displayFileName(value) || String(value || "").trim()),
    displayFileName(location),
    displayFileName(url),
  ];
  const imageName = candidates.find(
    (name) => name && !isResolutionLabel(name, resolution) && /\.[a-z0-9]{2,5}$/i.test(name)
  );

  if (imageName) {
    return imageName;
  }

  return String(json?.name || json?.assetName || "").trim();
};

/** Loads a displayable image for one tag and resolution. Revoke object URLs when done. */
export const loadAssetImagePreview = async (assetId, tag, resolution) => {
  const { blob, headerType } = await readAssetBlob(assetId, tag, resolution);
  const filename = `${tag || "asset"}-${resolution || "file"}`;

  if (blob && !headerType.includes("json") && !headerType.startsWith("text/")) {
    const typedBlob = blob.type ? blob : new Blob([blob], { type: headerType });
    const file = await fileFromBlob(typedBlob, filename);

    if (file) {
      return { url: URL.createObjectURL(file), revoke: true, file, fileName: file.name };
    }
  }

  if (!blob?.text) {
    return { url: "", revoke: false, file: null, fileName: "" };
  }

  try {
    const json = JSON.parse(await blob.text());
    const resolutionMap = json?.resolutions;
    const location = resolutionMap && typeof resolutionMap === "object" && !Array.isArray(resolutionMap)
      ? resolutionMap[resolution]
      : "";
    const directUrl = resolveAssetViewUrl(json);
    const url = httpUrl(directUrl) ? directUrl : resolveResolutionAssetUrl(json, resolution);

    return {
      url,
      revoke: false,
      file: null,
      fileName: storedImageName(json, resolution, location, url),
    };
  } catch {
    return { url: "", revoke: false, file: null, fileName: "" };
  }
};

const unwrapAssetList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (!data || typeof data !== "object") {
    return [];
  }

  const candidates = [data.assets, data.content, data.items, data.records, data.data, data.result];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (candidate && typeof candidate === "object") {
      const nested = candidate.assets || candidate.content || candidate.items || candidate.records;
      if (Array.isArray(nested)) {
        return nested;
      }
    }
  }

  return [];
};

const normalizeAssetListItem = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const assetId = String(item.assetId || item.id || "").trim();

  if (!assetId) {
    return null;
  }

  return {
    assetId,
    name: String(item.name ?? item.assetName ?? "").trim(),
    description: String(item.description ?? "").trim(),
    tags: parseAssetTagMetadata(item).filter((entry) => entry.tag),
  };
};

const isAssetIdQuery = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/** GET .../appsupport/assets?page=&pageSize=&name=&assetId= */
export const fetchAllAssetMetadata = async ({ page = 1, pageSize = 20, name, assetId } = {}) => {
  const params = { page, pageSize };
  const trimmedName = name?.trim();
  const trimmedAssetId = assetId?.trim();

  if (trimmedName) {
    params.name = trimmedName;
  }

  if (trimmedAssetId) {
    params.assetId = trimmedAssetId;
  }

  const response = await axios.get(getAssetsUrl(), {
    headers: {
      "Content-Type": "application/json",
    },
    params,
  });

  return unwrapAssetList(response?.data)
    .map(normalizeAssetListItem)
    .filter(Boolean);
};

/** Name search uses the list API name query, which matches partially. */
export const loadAssetSuggestions = async ({ name = "", pageSize = 20 } = {}) => {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return [];
  }

  if (isAssetIdQuery(trimmedName)) {
    return fetchAllAssetMetadata({ page: 1, pageSize: 1, assetId: trimmedName });
  }

  return fetchAllAssetMetadata({ page: 1, pageSize, name: trimmedName });
};
